import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import type { Memory, Child } from '@/types';

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function generateThumbnail(file: File, maxWidth = 300): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Not an image file'));
      return;
    }
    
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } else {
        reject(new Error('Could not get canvas context'));
      }
      URL.revokeObjectURL(url);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(url);
    };
    
    img.src = url;
  });
}

export async function exportMemoriesToPDF(
  memories: Memory[],
  children: Child[],
  title: string = '家庭时光记录'
): Promise<void> {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let y = margin;
  
  // 标题
  doc.setFontSize(20);
  doc.setTextColor(255, 140, 105);
  doc.text(title, pageWidth / 2, y, { align: 'center' });
  y += 15;
  
  // 生成日期
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`导出日期: ${new Date().toLocaleDateString('zh-CN')}`, pageWidth / 2, y, { align: 'center' });
  y += 20;
  
  for (const memory of memories) {
    // 检查是否需要新页面
    if (y > pageHeight - 40) {
      doc.addPage();
      y = margin;
    }
    
    const child = children.find((c) => c.id === memory.childId);
    
    // 日期和孩子
    doc.setFontSize(12);
    doc.setTextColor(255, 140, 105);
    const dateText = new Date(memory.createdAt).toLocaleDateString('zh-CN');
    doc.text(`${dateText} · ${child?.name || '未知'}`, margin, y);
    y += 8;
    
    // 内容
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const splitContent = doc.splitTextToSize(memory.content, pageWidth - margin * 2);
    doc.text(splitContent, margin, y);
    y += splitContent.length * 5 + 10;
    
    // 分隔线
    doc.setDrawColor(230, 230, 230);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;
  }
  
  doc.save(`${title}_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportMemoriesToZip(
  memories: Memory[],
  children: Child[]
): Promise<void> {
  const zip = new JSZip();
  
  // 添加 JSON 数据
  const data = JSON.stringify({ memories, children, exportDate: new Date().toISOString() }, null, 2);
  zip.file('memories.json', data);
  
  // 添加文本记录
  let textContent = '家庭时光记录\n';
  textContent += `导出日期: ${new Date().toLocaleDateString('zh-CN')}\n\n`;
  textContent += '='.repeat(50) + '\n\n';
  
  for (const memory of memories) {
    const child = children.find((c) => c.id === memory.childId);
    const date = new Date(memory.createdAt).toLocaleString('zh-CN');
    textContent += `日期: ${date}\n`;
    textContent += `孩子: ${child?.name || '未知'}\n`;
    textContent += `内容: ${memory.content}\n`;
    textContent += `标签: ${memory.tags.join(', ') || '无'}\n`;
    textContent += '-'.repeat(50) + '\n\n';
  }
  
  zip.file('memories.txt', textContent);
  
  // 生成并下载
  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `家庭时光备份_${new Date().toISOString().split('T')[0]}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadJSON(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function readJSONFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}
